import type { Dispatch, FC, SetStateAction } from 'react';
import type React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * The central filter data type.
 *
 * Simple mapping from attributes (strings) to values (FilterValue objects).
 * Since the same attribute can be filtered for multiple values (usually using OR, when actually applying the filter)
 * multiple values can be specified per attribute.
 */
export type FilterGroups = Readonly<Record<string, FilterValues>>;

/**
 * A value that's being filtered
 * For a better TypeScript experience, the filter expression is typed as a generic type T. This way, the filter
 * expression can be typed according to the actual use case.
 * */
export type FilterValue<T = Readonly<Record<string, string>>> = {
  /** A (usually) human readable string - translation keys are also a good candidate for labels. */
  readonly label: string;

  /**
   * An object, representing the filter value usually in a form that's appropriate for an API. A simple string as the
   * filter expression would not suffice, if for example nested object are filtered:
   *
   * @example { module_id: 5, has_access: true }
   */
  readonly filterExpr: T;
};

/** The values of one filter group, immutable like the state they come from. */
export type FilterValues = readonly FilterValue[];

/** Specifies if a filter can appear multiple times or is required. */
export interface FilterValueSpec {
  /**
   * The name of the filter, this spec applies to.
   *
   * Using a regular expression makes it possible to have the same spec apply for a group of filters.
   */
  multiple: boolean;
  name: string | RegExp;
  required: boolean;
  hideFilterChips?: boolean;
  /** Keep this group separate from mutually exclusive filter selection. */
  independent?: boolean;
}

export interface FilterContextValue {
  /** Add the filter value for the given name, keeping previous values. */
  addFilter(name: string, value: FilterValue): void;

  /** Currently active specs */
  filterSpecs: FilterValueSpec[];
  /** Currently active filter groups */
  filterGroups: FilterGroups;

  /** Returns the first spec, that matches the given name, or undefined. */
  getSpec(name: string): FilterValueSpec | undefined;

  /** Register a callback that will be called when the filters are reset. */
  registerOnResetCallback(callback: () => void): () => void;

  /** Removes the specified filter value. */
  removeFilter(name: string, valueLabel: string): void;

  /** Resets the filters to their default value or to the provided properties. */
  reset(props?: {
    defaultFilterGroups?: FilterGroups;
    filterSpecs?: FilterValueSpec[];
  }): void;

  /** Restore one group's initial value without resetting other groups. */
  resetFilter(name: string): void;

  /** Set the filter values for the given name, overriding any previous values. */
  setFilter(name: string, values: FilterValues): void;

  /** Direct setter for the filter groups state */
  setFilterGroups: Dispatch<SetStateAction<FilterGroups>>;

  /** Toggle the state of particular filter value. Useful for an onClick handler. */
  toggleFilter(name: string, value: FilterValue): void;

  /** Filter groups that have values and are not hidden */
  visibleFilterGroups: FilterGroups;
}

export const defaultFilterContextValue: FilterContextValue = {
  addFilter() {},
  filterSpecs: [],
  filterGroups: {},
  getSpec() {
    return undefined;
  },
  registerOnResetCallback() {
    return () => {};
  },
  removeFilter() {},
  reset() {},
  resetFilter() {},
  setFilter() {},
  setFilterGroups() {},
  toggleFilter() {},
  visibleFilterGroups: {},
};

/**
 * Simple context, that provides filters grouped by name.
 * The filters are an object, where the object-keys identify the attribute that is being
 * filtered and the object-values are the values of the attributes.
 *
 * To keep it simple, there is no additional nesting. So the filters provide an easy solution, when
 * there is only one level of nesting needed.
 *
 * The possible filter values are specified using a FilterValueSpec, which can be used to configure
 * the context. Using this specification it is possible to specify which filters are required or
 * allowed multiple times.
 *
 * The filter context doesn't have any filtering capabilities by itself. It's merely the data store
 * for whatever business logic needs to be built on top of it.
 *
 * @example
 *  const filterGroups = {
 *   hasGetReportAccess: [
 *     {
 *       label: 'Has getReport Access',
 *       filterExpr: { module_id: 1, has_access: true },
 *     },
 *   ],
 *   businessUnit: [
 *     { label: 'Outpost', filterExpr: { business_unit: 1 } },
 *     { label: 'Headquarters', filterExpr: { business_unit: 2 } },
 *   ],
 * };
 */
export const FilterContext = createContext<FilterContextValue>(
  defaultFilterContextValue,
);

/**
 * Returns the filter groups reduced to the orgUnit, independent groups, and the named group.
 * Used when combined filters are not allowed.
 */
const removeMultipleSpecs = (
  filterGroups: FilterGroups,
  name: string,
  getSpec: FilterContextValue['getSpec'],
): FilterGroups =>
  Object.fromEntries(
    Object.entries(filterGroups).filter(
      ([attribute]) =>
        attribute === 'orgUnit' ||
        attribute === name ||
        getSpec(attribute)?.independent,
    ),
  );

export const useFilterState = ({
  initialFilterGroups,
  initialFilterSpecs,
  allowCombinedFilters,
}: {
  initialFilterGroups: FilterGroups;
  initialFilterSpecs: FilterValueSpec[];
  allowCombinedFilters: boolean;
}): FilterContextValue => {
  const [filterSpecs, setFilterSpecs] = useState(initialFilterSpecs);
  const [filterGroups, setFilterGroups] = useState(initialFilterGroups);
  const [visibleFilterGroups, setVisibleFilterGroups] = useState<FilterGroups>(
    {},
  );
  const [defaultFilterGroups, setDefaultFilterGroups] =
    useState(initialFilterGroups);
  const onResetCallbacksRef = useRef<Set<() => void>>(new Set());

  const getSpec = useCallback(
    (name: string): FilterValueSpec | undefined => {
      if (!filterSpecs.length) return undefined;

      const spec = filterSpecs.find((spec) =>
        spec.name instanceof RegExp ? spec.name.test(name) : spec.name === name,
      );

      if (!spec) {
        console.error(`Cannot find spec for filter named ${name}.`);
        return undefined;
      }

      return spec;
    },
    [filterSpecs],
  );

  const setFilter = useCallback(
    (name: string, values: FilterValues) => {
      const spec = getSpec(name);

      if (!spec) return;

      setFilterGroups((previousFilterGroups) => {
        if (values.length === 0) {
          const updatedFilterGroups = { ...previousFilterGroups };
          delete updatedFilterGroups[name];
          return updatedFilterGroups;
        }

        const baseFilterGroups =
          !allowCombinedFilters && name !== 'orgUnit' && !spec.independent
            ? removeMultipleSpecs(previousFilterGroups, name, getSpec)
            : previousFilterGroups;

        return { ...baseFilterGroups, [name]: values };
      });
    },
    [allowCombinedFilters, getSpec],
  );

  const addFilter = useCallback(
    (name: string, value: FilterValue) => {
      const spec = getSpec(name);
      if (!spec) return;

      setFilterGroups((previousFilterGroups) => {
        if (name in previousFilterGroups) {
          // return unchanged filterGroup if adding multiple filters
          // of the same name is not allowed
          if (!spec.multiple) return previousFilterGroups;

          // append filter to existing entry
          return {
            ...previousFilterGroups,
            [name]: [...previousFilterGroups[name], value],
          };
        }
        // filter is not in filterGroups yet, add it
        // replace if filter is not allowed to be combined or is not independet
        const baseFilterGroups =
          allowCombinedFilters || spec.independent
            ? previousFilterGroups
            : removeMultipleSpecs(previousFilterGroups, name, getSpec);

        return { ...baseFilterGroups, [name]: [value] };
      });
    },
    [allowCombinedFilters, getSpec],
  );

  const removeFilter = useCallback(
    (name: string, valueLabel: string) => {
      const spec = getSpec(name);
      if (!spec) return;

      setFilterGroups((previousFilterGroups) => {
        const values = previousFilterGroups[name];
        const valueIndex = values?.findIndex(
          (filterValue) => filterValue.label === valueLabel,
        );

        if (!values || valueIndex < 0) return previousFilterGroups;

        if (spec.required && values.length === 1) {
          console.warn('Cannot remove last value of a required filter.');
          return previousFilterGroups;
        }

        const updatedFilterGroups = { ...previousFilterGroups };
        if (values.length === 1) {
          delete updatedFilterGroups[name];
        } else {
          updatedFilterGroups[name] = values.filter(
            (_, index) => index !== valueIndex,
          );
        }

        return updatedFilterGroups;
      });
    },
    [getSpec],
  );

  const toggleFilter = useCallback(
    (name: string, value: FilterValue) => {
      const spec = getSpec(name);

      if (
        filterGroups[name]?.findIndex(
          (filterValue) => filterValue.label === value.label,
        ) >= 0
      ) {
        removeFilter(name, value.label);
      } else {
        if (!spec || spec.multiple) {
          addFilter(name, value);
        } else {
          setFilter(name, [value]);
        }
      }
    },
    [addFilter, filterGroups, getSpec, removeFilter, setFilter],
  );

  const reset = useCallback(
    (props?: {
      defaultFilterGroups?: FilterGroups;
      filterSpecs?: FilterValueSpec[];
    }) => {
      setDefaultFilterGroups(props?.defaultFilterGroups || defaultFilterGroups);
      setFilterGroups(props?.defaultFilterGroups || defaultFilterGroups);
      setFilterSpecs(props?.filterSpecs || filterSpecs);

      onResetCallbacksRef.current.forEach((callback) => callback());
    },
    [defaultFilterGroups, filterSpecs],
  );

  const resetFilter = useCallback(
    (name: string) => {
      setFilterGroups((previousFilterGroups) => {
        // reset if there is a default-value
        const initialValues = defaultFilterGroups[name];
        if (initialValues) {
          return { ...previousFilterGroups, [name]: initialValues };
        }

        const updatedFilterGroups = { ...previousFilterGroups };
        // delete if there is no default-value
        delete updatedFilterGroups[name];
        return updatedFilterGroups;
      });
    },
    [defaultFilterGroups],
  );

  const registerOnResetCallback = useCallback((callback: () => void) => {
    onResetCallbacksRef.current.add(callback);

    // Return an unregister function,
    // please remember to call it when the component is unmounted to prevent memory leaks
    return () => {
      onResetCallbacksRef.current.delete(callback);
    };
  }, []);

  useEffect(() => {
    // Check if specs are violated
    filterSpecs.forEach((spec) => {
      const filterGroup = Object.entries(filterGroups).find(([name]) =>
        spec.name instanceof RegExp ? spec.name.test(name) : spec.name === name,
      );

      if (!filterGroup) {
        if (spec.required) {
          console.error(`Initial filter value for ${spec.name} is required.`);
        }
        return;
      }
    });

    // check that all initial filters conform to their spec
    Object.entries(initialFilterGroups).forEach(([name, values]) => {
      const spec = getSpec(name);

      if (spec && !spec.multiple && values.length > 1) {
        console.error(
          `Multiple initial filter values for ${name} specified, when only one is allowed.`,
        );
      }
    });
  }, [filterSpecs, filterGroups, initialFilterGroups, getSpec]);

  useEffect(() => {
    const filteredFilterGroups = Object.entries(filterGroups).filter(
      ([name, values]) => {
        const spec = getSpec(name);
        return !spec?.hideFilterChips && values.length > 0;
      },
    );

    setVisibleFilterGroups(Object.fromEntries(filteredFilterGroups));
  }, [filterGroups, getSpec]);

  return useMemo(
    () => ({
      addFilter,
      filterGroups,
      filterSpecs,
      getSpec,
      registerOnResetCallback,
      removeFilter,
      reset,
      resetFilter,
      setFilter,
      setFilterGroups,
      visibleFilterGroups,
      toggleFilter,
    }),
    [
      addFilter,
      filterGroups,
      filterSpecs,
      getSpec,
      registerOnResetCallback,
      removeFilter,
      reset,
      resetFilter,
      setFilter,
      setFilterGroups,
      visibleFilterGroups,
      toggleFilter,
    ],
  );
};

interface FilterProviderProps {
  children: React.ReactNode;
  initialFilterSpecs?: FilterValueSpec[];
  initialFilterGroups?: FilterGroups;
  allowCombinedFilters?: boolean;
}

export const FilterProvider: FC<FilterProviderProps> = ({
  children,
  initialFilterGroups = {},
  initialFilterSpecs = [],
  allowCombinedFilters = false,
}) => {
  const value = useFilterState({
    initialFilterGroups,
    initialFilterSpecs,
    allowCombinedFilters,
  });

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
};

export const useFilterContext = () =>
  useContext<FilterContextValue>(FilterContext);
